import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlayCircle, User, Briefcase, Calendar, ArrowRight,
  HelpCircle, ChevronDown, FileText, Sparkles, Mail
} from "lucide-react";

// YouTube placeholder (easy to replace later)
const YT_PLACEHOLDER = "https://www.youtube.com/embed/dQw4w9WgXcQ";

const SUPPORT_EMAIL = "contact@club-business-maroc.com";

const VIDEOS = {
  intro: YT_PLACEHOLDER,
  profile: YT_PLACEHOLDER,
  opportunities: YT_PLACEHOLDER,
  events: YT_PLACEHOLDER,
  generate: YT_PLACEHOLDER,
};

const FAQ = [
  {
    q: "Comment rejoindre Club Business Maroc ?",
    a: "L'accès est sur invitation. Un membre actif du club peut vous parrainer via le bouton \"Parrainer un membre\" dans sa sidebar. Votre profil sera validé par l'équipe avant activation.",
  },
  {
    q: "Qui peut publier une annonce ?",
    a: "Tous les membres actifs peuvent publier jusqu'à 4 annonces par mois (types : Je cherche, Je propose, Opportunité). Un profil doit être complété à 80% minimum pour apparaître dans l'annuaire.",
  },
  {
    q: "Comment contacter un membre ?",
    a: "Depuis l'annuaire ou une fiche profil, utilisez le bouton \"Contacter\" (WhatsApp, LinkedIn ou email selon ce que le membre rend visible). Chaque contact est comptabilisé dans vos statistiques.",
  },
  {
    q: "Qu'est-ce qu'un avantage membre ?",
    a: "C'est une offre exclusive que vous proposez aux autres membres du club (remise, ressource gratuite, consultation offerte…). Limite : un avantage actif par membre, modéré par l'équipe.",
  },
  {
    q: "Les événements sont-ils gratuits ?",
    a: "La majorité des événements et replays sont réservés aux membres actifs. Certains événements premium peuvent avoir un accès payant, indiqué clairement sur la fiche de l'événement.",
  },
];

function VideoBlock({ src, title }) {
  return (
    <div
      className="relative w-full rounded-xl overflow-hidden"
      style={{
        paddingBottom: "56.25%",
        background: "#0D1B3E",
        border: "1px solid rgba(201,162,39,0.2)",
      }}
    >
      <iframe
        className="absolute inset-0 w-full h-full"
        src={src}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function QuickStartCard({ icon: Icon, title, description, cta, onClick, video, testId }) {
  return (
    <div className="cbm-card p-5 flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: "rgba(201,162,39,0.14)",
            border: "1px solid rgba(201,162,39,0.35)",
          }}
        >
          <Icon size={18} className="text-gold" />
        </div>
        <h3
          className="font-outfit font-semibold text-white"
          style={{ fontSize: "1.0625rem", letterSpacing: "-0.01em" }}
        >
          {title}
        </h3>
      </div>
      <p className="text-cbm-secondary text-sm leading-relaxed">{description}</p>
      <VideoBlock src={video} title={title} />
      <button
        onClick={onClick}
        className="cbm-btn-primary text-sm mt-auto"
        data-testid={`${testId}-cta`}
      >
        {cta} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function FAQItem({ q, a, open, onToggle, testId }) {
  return (
    <div
      className="cbm-card p-0 overflow-hidden transition-colors"
      style={{ borderColor: open ? "rgba(201,162,39,0.4)" : undefined }}
      data-testid={testId}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 text-left px-4 py-3.5 hover:bg-white/2 transition-colors"
      >
        <span className="text-white text-sm font-medium flex-1">{q}</span>
        <ChevronDown
          size={16}
          className="text-gold flex-shrink-0 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0">
          <p className="text-cbm-secondary text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function Demarrage() {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in" data-testid="demarrage-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="cbm-section-title">Démarrage</h1>
          <p className="text-cbm-muted text-sm mt-1">
            Prenez 5 minutes pour comprendre comment tirer le meilleur du club.
          </p>
        </div>
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Aide%20Club%20Business%20Maroc`}
          className="cbm-btn-ghost text-sm border border-white/10 hover:border-gold/50"
          data-testid="support-button"
        >
          <HelpCircle size={14} /> Besoin d'aide ?
        </a>
      </div>

      {/* Section 1 — Introduction */}
      <section className="mb-8 animate-fade-in-up" data-testid="section-intro">
        <div className="cbm-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-gold" />
            <p className="cbm-label">Introduction</p>
          </div>
          <h2
            className="font-outfit font-bold text-white mb-2"
            style={{ fontSize: "1.75rem", letterSpacing: "-0.02em", lineHeight: 1.15 }}
          >
            Bienvenue dans Club Business Maroc
          </h2>
          <p className="text-cbm-secondary text-base mb-5 max-w-2xl">
            Une plateforme pour créer des opportunités business entre dirigeants.
          </p>
          <VideoBlock src={VIDEOS.intro} title="Bienvenue" />
        </div>
      </section>

      {/* Section 2 — Quick Start */}
      <section className="mb-8 animate-fade-in-up-delay-1" data-testid="section-quickstart">
        <div className="mb-4">
          <h2 className="font-outfit font-bold text-white text-xl" style={{ letterSpacing: "-0.02em" }}>
            Démarrage rapide
          </h2>
          <p className="text-cbm-muted text-sm mt-0.5">3 étapes pour activer votre présence sur le club.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickStartCard
            icon={User}
            title="Compléter votre profil"
            description="Un profil complet est visible dans l'annuaire et inspire confiance. Nom, entreprise, secteur, offre, contact."
            video={VIDEOS.profile}
            cta="Compléter mon profil"
            onClick={() => navigate("/mon-profil")}
            testId="quickstart-profile"
          />
          <QuickStartCard
            icon={Briefcase}
            title="Trouver et publier des annonces"
            description="Publiez ce que vous cherchez ou proposez, et consultez les annonces des autres membres pour créer du business."
            video={VIDEOS.opportunities}
            cta="Voir les annonces"
            onClick={() => navigate("/opportunites")}
            testId="quickstart-opportunities"
          />
          <QuickStartCard
            icon={Calendar}
            title="Participer aux événements"
            description="Webinaires, afterworks et workshops : rencontrez les autres membres et développez votre réseau."
            video={VIDEOS.events}
            cta="Voir les événements"
            onClick={() => navigate("/evenements")}
            testId="quickstart-events"
          />
        </div>
      </section>

      {/* Section 3 — Comment générer des opportunités */}
      <section className="mb-8 animate-fade-in-up-delay-2" data-testid="section-generate">
        <div className="cbm-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle size={14} className="text-gold" />
            <p className="cbm-label">Guide</p>
          </div>
          <h2
            className="font-outfit font-bold text-white mb-4"
            style={{ fontSize: "1.5rem", letterSpacing: "-0.02em" }}
          >
            Comment générer des opportunités concrètes
          </h2>
          <VideoBlock src={VIDEOS.generate} title="Générer des opportunités" />
        </div>
      </section>

      {/* Section 4 — FAQ */}
      <section className="mb-8 animate-fade-in-up-delay-3" data-testid="section-faq">
        <div className="mb-4">
          <h2 className="font-outfit font-bold text-white text-xl" style={{ letterSpacing: "-0.02em" }}>
            Questions fréquentes
          </h2>
        </div>
        <div className="space-y-2">
          {FAQ.map((item, i) => (
            <FAQItem
              key={i}
              q={item.q}
              a={item.a}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              testId={`faq-${i}`}
            />
          ))}
        </div>
      </section>

      {/* Section 5 — Charte */}
      <section className="animate-fade-in-up-delay-3" data-testid="section-charter">
        <div
          className="cbm-card p-6 flex items-center gap-4 flex-wrap"
          style={{
            borderLeftWidth: "3px",
            borderLeftColor: "#C9A227",
          }}
        >
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: "rgba(201,162,39,0.16)",
              border: "1px solid rgba(201,162,39,0.4)",
            }}
          >
            <FileText size={18} className="text-gold" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <h3
              className="font-outfit font-bold text-white mb-1"
              style={{ fontSize: "1.0625rem" }}
            >
              Charte du club
            </h3>
            <p className="text-cbm-secondary text-sm">
              Les valeurs et règles qui garantissent la qualité des échanges entre membres.
            </p>
          </div>
          <button
            onClick={() => window.open("/charte", "_blank")}
            className="cbm-btn-secondary text-sm flex-shrink-0"
            data-testid="charter-cta"
          >
            <FileText size={14} /> Voir la charte
          </button>
        </div>
      </section>

      {/* Footer support hint */}
      <div className="mt-8 text-center">
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Aide%20Club%20Business%20Maroc`}
          className="text-cbm-muted text-sm hover:text-gold transition-colors inline-flex items-center gap-1.5"
          data-testid="footer-support"
        >
          <Mail size={13} /> Une question ? {SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  );
}
